import React, { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { useQueryClient } from "react-query"
import { saveTicketPurchaseBandInterests } from "../../../requests/ticketPurchases"
import { ticketPurchaseQueryKeys } from "../../../hooks/useTicketPurchase"
import type { EventPerformanceType } from "../../../types/events"

interface BandInterestPromptProps {
  isOpen: boolean
  postId: number
  ticketPurchaseId: number | null | undefined
  eventPerformances: EventPerformanceType[] | null | undefined
  // Required for a guest checkout purchase (no JWT session yet) - this
  // specific ticket's own ticket_id, which proves control of just this one
  // purchase. Omit for an authenticated purchase, where the normal JWT
  // applies.
  guestToken?: string | null
  // Pre-selects boxes for interests already saved on this ticket purchase -
  // e.g. when reopening from Manage Tickets, rather than the fresh
  // right-after-purchase prompt where nothing is selected yet.
  initialSelectedIds?: number[]
}

// Mirrors EventPerformance's own `active` scope (confirmed/pending/stale)
// rather than just "confirmed" - most real bills are mostly "pending" acts
// (booked, not yet linked to a band account), and "stale" just means the
// band stopped responding to invite notifications, not that they're off
// the bill. Only declined/tentative performers are excluded.
//
// Exported so callers that wrap this in their own dialog (e.g.
// TicketPurchaseComponent) can check ahead of time whether there's actually
// a >=2 picker to show, rather than opening an empty dialog around a
// component that's about to render null.
export const qualifyingBandInterestLineup = (
  eventPerformances: EventPerformanceType[] | null | undefined,
): EventPerformanceType[] =>
  (eventPerformances || []).filter(
    (performance) =>
      ["confirmed", "pending", "stale"].includes(performance.status) &&
      performance.band_name,
  )

// Simple tap-to-toggle grid of "which band(s) did you buy this ticket to
// see" - rendered inline within the ticket confirmation (not its own
// dialog). Each tap saves/removes the selection immediately, no separate
// submit step. A Breakout-style pick-by-smashing-the-name game is planned as
// a fun follow up, but this plain grid is the first version.
export const BandInterestPrompt: React.FC<BandInterestPromptProps> = ({
  isOpen,
  postId,
  ticketPurchaseId,
  eventPerformances,
  guestToken,
  initialSelectedIds,
}) => {
  const [activeIds, setActiveIds] = useState<number[]>(initialSelectedIds || [])
  const [savingId, setSavingId] = useState<number | null>(null)
  const queryClient = useQueryClient()
  const autoSavedForTicketIdRef = useRef<number | null>(null)

  const lineup = qualifyingBandInterestLineup(eventPerformances)

  // With only one qualifying performer there's no real choice to present -
  // a picker UI needs >=2 options to make sense - but we still assume
  // that's who the ticket was bought for and record it, rather than
  // silently dropping the data point entirely.
  useEffect(() => {
    if (!isOpen || !ticketPurchaseId || lineup.length !== 1) return
    if (activeIds.length > 0) return
    if (autoSavedForTicketIdRef.current === ticketPurchaseId) return

    const soleId = lineup[0].id as number
    autoSavedForTicketIdRef.current = ticketPurchaseId
    saveTicketPurchaseBandInterests(postId, ticketPurchaseId, [soleId], guestToken || undefined)
      .then((response) => {
        if (response.success) {
          setActiveIds([soleId])
          queryClient.invalidateQueries(ticketPurchaseQueryKeys.myTickets(postId))
        } else {
          autoSavedForTicketIdRef.current = null
        }
      })
      .catch(() => {
        autoSavedForTicketIdRef.current = null
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticketPurchaseId, lineup.length, activeIds.length])

  if (!isOpen || !ticketPurchaseId || lineup.length < 2) return null

  const handleToggle = async (performanceId: number) => {
    if (savingId !== null) return

    const isActive = activeIds.includes(performanceId)
    const newIds = isActive
      ? activeIds.filter((id) => id !== performanceId)
      : [...activeIds, performanceId]

    setSavingId(performanceId)
    try {
      const response = await saveTicketPurchaseBandInterests(
        postId,
        ticketPurchaseId,
        newIds,
        guestToken || undefined,
      )
      if (response.success) {
        setActiveIds(newIds)
        // event_performance_interest_ids on the cached ticket purchase is
        // now stale - without this, reopening Manage Tickets would
        // re-mount this component with the pre-edit selection.
        queryClient.invalidateQueries(ticketPurchaseQueryKeys.myTickets(postId))
      } else {
        toast.error(response.error || "Couldn't save your selection")
      }
    } catch (error) {
      toast.error("Couldn't save your selection")
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="mt-6 mb-6 pt-4 border-t border-gray-200">
      <h2 className="font-bold text-black-800 mb-4">
        Who are you trying to see?
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {lineup.map((performance) => {
          const performanceId = performance.id as number
          const isActive = activeIds.includes(performanceId)
          const isSaving = savingId === performanceId
          const coverImageUrl = performance.band?.cover_image_url

          return (
            <button
              key={performanceId}
              type="button"
              onClick={() => handleToggle(performanceId)}
              disabled={isSaving}
              className="relative h-24 rounded-sm border-2 overflow-hidden flex items-center justify-center px-2 py-3 text-center transition-all focus:outline-none disabled:opacity-50"
              style={{
                borderColor: isActive ? "#2f855a" : "#d1d5db",
                backgroundImage: coverImageUrl
                  ? `url(${coverImageUrl})`
                  : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: isActive ? "none" : "grayscale(1)",
                opacity: isActive ? 1 : 0.5,
                boxShadow: isActive ? "0 0 12px 2px rgba(47, 133, 90, 0.7)" : "none",
              }}
            >
              {coverImageUrl && (
                <div className="absolute inset-0 bg-black bg-opacity-40" />
              )}
              <span
                className="relative block max-w-full truncate text-lg font-bold"
                style={{
                  color:
                    coverImageUrl ? "#fff" : isActive ? "#2f855a" : "#1a202c",
                }}
              >
                {performance.band_name}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default BandInterestPrompt
